import dayjs from 'dayjs';

import type {CourseApiModel, CourseExpandedCommon} from '../../../api/course';
import type {CoreCoachCoursesPauseParams} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {conferenceStore} from '../../../api/conference';
import {courseExpandCommon, courseStore} from '../../../api/course';
import {ISOStringToDatetimeObj} from '../../../utils/datetime';
import {isCourseIndividual} from '../../../utils/entity/course';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {AppError} from '../../../utils/errors/app';
import {conferenceFetchList} from '../conference/conference.fetch-list';

export {coursePause};

export const COURSE_PAUSED_CONFERENCE_DELETING_REASON = 'course_paused';

type Config = BaseConfig<Omit<CoreCoachCoursesPauseParams, '_id'>> & {
  entity: CourseApiModel;
};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  if (isMeCoachForEntity(config.entity)) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function coursePause(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

coursePause.asClient = asClient;
coursePause.asCoach = asCoach;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({entity, params}: Config) {
  if (entity.status !== 'ongoing') {
    throw new AppError("course can't be paused");
  }

  const pausedCourse = (await courseStore.pause(entity, {
    ...params,
    expand: courseExpandCommon,
  })) as CourseExpandedCommon;

  const {suspend} = params;

  /**
   * For individual courses, we need to cancel booked conferences by hand
   */
  if (isCourseIndividual(pausedCourse) && suspend?.includes('conferences')) {
    const conferences = await conferenceFetchList.asCoach({
      asClient: {params: {}},
      asCoach: {
        fetchMyClientConferences: false,
        params: {
          limit: 999,
          query: [
            ['course_id', '==', pausedCourse._id],
            ['status', '==', 'upcoming'],
            ['created_by', '==', 'client'],
            ['start_date', '>', ISOStringToDatetimeObj(dayjs().toISOString())],
          ],
        },
      },
    });

    if (conferences.length) {
      await conferenceStore.coach.deleteBatch(
        conferences.map(conference => ({
          entity: conference,
          params: {
            reason: COURSE_PAUSED_CONFERENCE_DELETING_REASON,
          },
        })),
      );
    }
  }

  courseStore.localUpdate(entity, pausedCourse);

  return pausedCourse;
}
